"use client"

import type { ItemDefinition, ItemEffect } from "@/types/item.types"

// Rarity colors and labels
const RARITY_CONFIG = {
  common: { color: "#9CA3AF", label: "Common" },
  rare: { color: "#3B82F6", label: "Rare" },
  epic: { color: "#A855F7", label: "Epic" },
  legendary: { color: "#F59E0B", label: "Legendary" },
  unique: { color: "#F97316", label: "Unique" },
}

// Must track ItemDefinition["category"]. This listed "equipment" and "scroll",
// which no item uses, while omitting the two that half the roster does use - so
// armor and weapon entries rendered a raw lowercase "weapon".
const CATEGORY_LABELS: Record<string, string> = {
  potion: "Potion",
  trinket: "Trinket",
  armor: "Armor",
  weapon: "Weapon",
}

// Signed so debuffs read correctly - several items trade a penalty for power
// (Ledger of Greed's -50% speed, Worldender Spear's -200 max HP).
const signed = (value: number) => `${value >= 0 ? "+" : "-"}${Math.abs(value)}`
const signedPercent = (value: number) =>
  `${value >= 0 ? "+" : "-"}${Math.abs(value * 100).toFixed(0)}%`

/**
 * Human-readable text for one item effect.
 *
 * Exhaustive over ItemEffectType on purpose: four cases here used to name types
 * that do not exist ("armor-boost", "max-mana-boost", ...) while ten real ones
 * were missing, so effects like lifesteal fell through to a raw
 * "lifesteal: 0.15". The `never` check below makes that impossible to
 * reintroduce - adding a type to the union fails the build until it is handled.
 */
const formatEffect = (effect: ItemEffect): string => {
  switch (effect.type) {
    case "heal": {
      // Some items pay for their power with a drain (Bloodbound Grimoire), which
      // "Restores -5 health" describes backwards
      const over = effect.duration ? ` over ${(effect.duration / 60).toFixed(1)}s` : " instantly"
      return effect.value < 0
        ? `Drains ${Math.abs(effect.value)} health${effect.duration ? ` every ${(effect.duration / 60).toFixed(1)}s` : ""}`
        : `Restores ${effect.value} health${over}`
    }
    case "mana":
      return `Restores ${effect.value} mana`
    case "damage-boost":
      return `${signed(effect.value)} damage`
    case "armor":
      return `${signed(effect.value)} armor`
    case "max-health":
      return `${signed(effect.value)} max health`
    case "speed-boost":
      return `${signedPercent(effect.value)} movement speed`
    case "attack-speed":
      return `${signedPercent(effect.value)} attack speed`
    case "crit-chance":
      return `${signedPercent(effect.value)} critical hit chance`
    case "spell-power":
      return `${signedPercent(effect.value)} skill damage`
    case "lifesteal":
      return `${signedPercent(effect.value)} lifesteal`
    case "gold-bonus":
      return `${signedPercent(effect.value)} gold from kills`
    case "damage-reflect":
      return `${signedPercent(effect.value)} of damage taken reflected`
    case "cooldown-reduction":
      return `-${Math.abs(effect.value * 100).toFixed(0)}% ability cooldowns`
    case "mana-regen":
      return `${signed(effect.value)} mana regeneration`
    case "hp-regen":
      return `${signed(effect.value)} health regeneration`
    case "absorb":
      return `Absorbs the next ${effect.value} damage${effect.duration ? ` for up to ${(effect.duration / 60).toFixed(0)}s` : ""}`
    case "teleport":
      return "Teleports you to an allied tower or the spawn point"
    case "lure":
      return `Draws nearby enemies to the target point${effect.duration ? ` for ${(effect.duration / 60).toFixed(0)}s` : ""}`
    default: {
      const exhaustive: never = effect.type
      return String(exhaustive)
    }
  }
}

interface ItemDetailsProps {
  item: ItemDefinition
}

export const ItemDetails = ({ item }: ItemDetailsProps) => {
  const rarityConfig = RARITY_CONFIG[item.rarity]

  return (
    <div id="item-details" className="space-y-4">
      {/* Large item image */}
      <div className="flex justify-center">
        <div
          className="relative w-32 h-32 rounded-lg overflow-hidden bg-slate-800 p-4"
          style={{
            boxShadow: `0 0 20px ${rarityConfig.color}40, inset 0 0 0 2px ${rarityConfig.color}`,
          }}
        >
          {item.image && (
            <img
              src={item.image}
              alt={item.name}
              className="w-full h-full object-contain"
              style={{ imageRendering: "pixelated" }}
            />
          )}
        </div>
      </div>

      {/* Item name */}
      <h2 className="text-2xl font-bold text-center" style={{ color: rarityConfig.color }}>
        {item.name}
      </h2>

      {/* Rarity and category */}
      <div className="flex items-center justify-center gap-3 text-sm">
        <span
          className="px-3 py-1 rounded-full font-bold"
          style={{
            backgroundColor: `${rarityConfig.color}20`,
            color: rarityConfig.color,
            border: `1px solid ${rarityConfig.color}`,
          }}
        >
          {rarityConfig.label}
        </span>
        <span className="text-gray-400">{CATEGORY_LABELS[item.category] || item.category}</span>
      </div>

      {/* Description */}
      <p className="text-gray-300 text-sm text-center italic">{item.description}</p>

      {/* Cost section */}
      {item.cost > 0 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <span className="text-gray-400">Cost:</span>
          <span className="text-green-400 font-bold text-lg">${item.cost}</span>
        </div>
      )}

      {/* Effects section */}
      {item.effects && item.effects.length > 0 && (
        <div className="space-y-2 pt-4 border-t border-amber-600/30">
          <h3 className="text-lg font-bold text-amber-400">Effects</h3>
          <div className="bg-black/30 rounded-lg p-3 space-y-1">
            {item.effects.map((effect, index) => (
              <div key={index} className="flex items-start gap-2">
                <span className="text-amber-500">•</span>
                <span className="text-gray-300 text-sm">{formatEffect(effect)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Additional info */}
      <div className="space-y-1 pt-2 border-t border-gray-700 text-xs text-gray-500">
        {item.stackable && (
          <div>Stackable (max {item.maxStack})</div>
        )}
        {item.cooldown && (
          <div>Cooldown: {(item.cooldown / 60).toFixed(1)}s</div>
        )}
        {item.type && (
          <div className="capitalize">{item.type}</div>
        )}
      </div>
    </div>
  )
}
